import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DaySnapshot } from "@/components/PnlCalendar";
import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errMsg, num, rupee, pnlClass, legText, dirClass } from "@/lib/api";

const LINE = { paper: "#94A3B8", dry: "#818CF8", live: "#EF4444" };
const hhmm = (ts) => new Date(ts * 1000).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false, hour: "2-digit", minute: "2-digit" });

const Timeline = ({ points, books }) => points.length < 2 ? (
  <div data-testid="pnl-timeline-empty" className="mt-2 text-[11px] text-slate-600">Running P&amp;L line starts once the market opens (sampled every minute).</div>
) : (
  <div className="mt-2 h-[84px]" data-testid="pnl-timeline">
    <ResponsiveContainer>
      <LineChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <XAxis dataKey="ts" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={hhmm} tick={{ fill: "#64748B", fontSize: 9 }} minTickGap={40} />
        <YAxis tick={{ fill: "#64748B", fontSize: 9 }} width={52} tickFormatter={(v) => rupee(Math.round(v))} />
        <ReferenceLine y={0} stroke="#334155" />
        <Tooltip labelFormatter={hhmm} formatter={(v, n) => [rupee(v), n]} contentStyle={{ background: "#121826", border: "1px solid #334155", fontSize: 11 }} />
        {books.map(([k, label]) => <Line key={k} dataKey={k} name={label} stroke={LINE[k]} dot={false} strokeWidth={k === "live" ? 2 : 1.5} isAnimationActive={false} />)}
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const ExitButton = ({ p, onDone }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/positions/${p.book}/${p.id}/exit`, { confirm: p.book === "live" });
      toast.success(`${p.book === "live" ? "LIVE" : p.book === "dry" ? "Dry-run" : "Paper"} spread exited · ${rupee(r.data.pnl ?? r.data.trade?.pnl)}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const btn = (props) => (
    <Button data-testid={`button-exit-${p.id}`} size="sm" variant="outline" disabled={busy} {...props}
      className={`h-6 gap-1 px-2 text-[11px] ${p.book === "live" ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "border-slate-600"}`}>
      <LogOut size={11} /> {busy ? "…" : "Exit"}
    </Button>
  );
  if (p.book !== "live") return btn({ onClick: go });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{btn({})}</AlertDialogTrigger>
      <AlertDialogContent className="z-[60] border-red-500/40 bg-[#121826] text-slate-100">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-400">Exit live spread now?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300">
            Sends real limit orders to Dhan to close every leg ({p.legs.map(legText).join(", ")}): short legs are bought back first, then long legs are sold, with price chasing. Current MTM {rupee(p.pnl)}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`button-exit-cancel-${p.id}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`button-exit-confirm-${p.id}`} onClick={go} className="bg-red-600 hover:bg-red-500">Exit at market</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const BOOKS = [["live", "Live"]];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false, hour: "2-digit", minute: "2-digit" }) : "—");

const Tile = ({ id, label, b, active, onClick, flash, onChip }) => (
  <div className={`flex min-w-[140px] flex-1 flex-wrap items-center gap-3 rounded border px-3 py-2 transition-colors ${active ? "border-indigo-500/60 bg-indigo-500/10" : "border-slate-800 bg-slate-900/50 hover:border-slate-600"} ${id === "live" && b.open ? "ring-1 ring-red-500/40" : ""}`}>
    <button data-testid={`day-pnl-${id}`} onClick={onClick} className="group flex items-center gap-3 text-left">
      <div>
        <div className="eyebrow !text-[10px]">{label} · day P&amp;L</div>
        <div data-testid={`day-pnl-${id}-total`} className={`num text-lg font-bold transition-colors duration-500 ${pnlClass(b.total)} ${flash ? "bg-slate-700/40" : ""}`}>{rupee(b.total)}</div>
        <div className="num text-[10px] text-slate-500">{b.open} open · {b.closed} closed · real {rupee(b.realized)} · MTM {rupee(b.unrealized)}</div>
      </div>
      <ChevronDown size={14} className={`text-slate-500 transition-transform ${active ? "rotate-180" : ""}`} />
    </button>
    <div className="ml-auto flex flex-wrap justify-end gap-1.5" data-testid="day-pnl-by-strategy">
      {(b.by_kind || []).map((k) => (
        <button key={k.kind} data-testid={`day-pnl-kind-${k.kind}`} onClick={() => onChip(k)} title="Show today's chart"
          className="flex items-center gap-1 rounded border border-slate-800 px-2 py-1 text-[11px] hover:border-indigo-500/60">
          <ImageIcon size={11} className="text-slate-500" />
          <span className="text-slate-400">{k.label}</span> <b className={`num ${pnlClass(k.total)}`}>{rupee(k.total)}</b>
          {k.open > 0 && <span className="text-[10px] text-amber-400">{k.open} open</span>}
        </button>
      ))}
      {!(b.by_kind || []).length && <span className="text-[11px] text-slate-500" data-testid="day-pnl-no-live">No live trades today</span>}
    </div>
  </div>
);

const LegCells = ({ l, open }) => (
  <>
    <td className={`!text-right ${l.side === "BUY" ? "up" : "down"}`}>{l.side}</td>
    <td>{l.strike == null ? "" : num(l.strike, 0)} {l.type}</td><td>{l.qty}</td><td>{num(l.entry)}</td>
    <td>{open ? num(l.ltp) : num(l.exit)}</td>
  </>
);

export const DayPnlStrip = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [flash, setFlash] = useState({});
  const [points, setPoints] = useState([]);
  const [chip, setChip] = useState(null);
  const prev = useRef({});
  const load = useCallback(() => api.get("/positions/today").then((r) => {
    const f = {};
    BOOKS.forEach(([k]) => { if (prev.current[k] !== undefined && prev.current[k] !== r.data.books[k].total) f[k] = true; prev.current[k] = r.data.books[k].total; });
    setFlash(f);
    if (Object.keys(f).length) setTimeout(() => setFlash({}), 700);
    setData(r.data);
    api.get("/positions/timeline").then((t) => setPoints(t.data.points)).catch(() => {});
  }).catch(() => {}), []);
  useEffect(() => {
    load();
    const id = setInterval(load, data?.market_open ? 5000 : 30000);
    return () => clearInterval(id);
  }, [load, data?.market_open]);
  if (!data) return null;
  const shown = BOOKS;
  const book = open && data.books[open];

  return (
    <div className="mb-2 px-1" data-testid="day-pnl-strip">
      <div className="flex flex-wrap gap-2">
        {shown.map(([k, label]) => <Tile key={k} id={k} label={label} b={data.books[k]} active={open === k} flash={flash[k]} onClick={() => setOpen(open === k ? null : k)} onChip={setChip} />)}
      </div>
      <Dialog open={!!chip} onOpenChange={(o) => !o && setChip(null)}>
        <DialogContent className="max-w-5xl border-slate-700 bg-[#121826] text-slate-100" data-testid="dialog-day-chart">
          <DialogHeader>
            <DialogTitle className="text-sm">NIFTY chart · {data.date}</DialogTitle>
            <DialogDescription className="text-[11px] text-slate-400">Fresh snapshot of the whole day with every trade marked. You opened it from {chip?.label} (live {rupee(chip?.total)} today).</DialogDescription>
          </DialogHeader>
          {chip && <DaySnapshot day={data.date} fresh />}
        </DialogContent>
      </Dialog>
      <Timeline points={points} books={shown} />
      {book && (
        <div className="mt-2 overflow-x-auto rounded border border-slate-800" data-testid={`day-positions-${open}`}>
          <table className="dense num w-full">
            <thead><tr><th>Time</th><th>Spread</th><th>Leg</th><th>Strike</th><th>Qty</th><th>Entry</th><th>LTP / Exit</th><th>Spread val</th><th>Stop / Tgt</th><th>Status</th><th>P&amp;L</th><th></th></tr></thead>
            <tbody>
              {book.positions.length === 0 && <tr><td colSpan={12} className="!py-5 !text-center text-slate-500">No {BOOKS.find(([k]) => k === open)[1].toLowerCase()} trades today{data.market_open ? " yet" : ""}.</td></tr>}
              {book.positions.map((p) => p.legs.map((l, i) => (
                <tr key={`${p.id}-${i}`} data-testid={i === 0 ? `day-position-${p.id}` : undefined} className={i === 0 ? "border-t border-slate-800" : ""}>
                  {i === 0 && <td rowSpan={p.legs.length}>{fmtTime(p.opened_at)}{p.closed_at ? `–${fmtTime(p.closed_at)}` : ""}</td>}
                  {i === 0 && <td rowSpan={p.legs.length} className={dirClass(p.direction)}>{p.strategy}<div className="text-[10px] text-slate-500">exp {p.expiry}</div></td>}
                  <LegCells l={l} open={p.status !== "closed"} />
                  {i === 0 && <td rowSpan={p.legs.length}>{num(p.debit)} → {num(p.value)}</td>}
                  {i === 0 && <td rowSpan={p.legs.length}><span className="down">{num(p.stop)}</span> / <span className="up">{num(p.target)}</span></td>}
                  {i === 0 && <td rowSpan={p.legs.length} className="uppercase text-slate-400">{p.status}{p.exit_reason ? ` · ${p.exit_reason}` : ""}</td>}
                  {i === 0 && <td rowSpan={p.legs.length} className={`font-semibold ${pnlClass(p.pnl)}`}>{rupee(p.pnl)}</td>}
                  {i === 0 && <td rowSpan={p.legs.length}>{p.status === "open" && <ExitButton p={p} onDone={load} />}</td>}
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
